import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { useCosmos } from 'providers/contexts/CosmosContext';
import SelectDropdown from 'components/SelectDropdown/SelectDropdown';
import { useEditorBubbleMenu } from 'components/EditorBubbleMenu/context';
import { AssetViewContext } from 'screens/editorial/asset/context/AssetViewContextProvider';
import { buildViewAtSiteUrl } from 'utility/functions';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import type { BinaryUploadResponseData } from 'utility/types/dataProvider';
import { CustomToolbarBtnProps } from '../types/customToolbarBtnTypes';
import { ARTICLE_BODY_COPY_ID } from 'screens/editorial/asset/utility/getEditorConfigs';

type LinkActionKey = 'url' | 'asset' | 'remove';

function openLinkEditor(
  editor: CustomToolbarBtnProps['editor'],
  setBubbleView?: (value: string | boolean) => void,
) {
  if (!editor) {
    return;
  }

  if (setBubbleView) {
    const { from, to } = editor.state.selection;
    setBubbleView('linkEditor');

    editor.storage.isToolbarToggle = true;

    queueMicrotask(() => {
      editor.commands.setTextSelection({ from, to: to - 1 });

      queueMicrotask(() => {
        editor.commands.setTextSelection({ from, to });
        setTimeout(() => {
          editor.storage.isToolbarToggle = false;
        }, 50);
      });
    });
    return;
  }

  editor.storage.bubbleMenu = { view: 'linkEditor' };
}

export default function LinkSelect({ editor, disabled }: CustomToolbarBtnProps) {
  const { t } = useTranslation();
  const { activeSite } = useCosmos();
  const assetViewContext = useContext(AssetViewContext);
  // Because richtext generic editor setup has the link bubble but also threads
  // we dont want threads to show up in places like user edit etc where
  // link is needed but not commenting threads and only show the threads on articles
  const element = editor?.options?.element;
  const isEditorialAndArticleContent =
    element && typeof element === 'object' && 'id' in element
      ? element.id === ARTICLE_BODY_COPY_ID
      : false;

  let setBubbleView: ((value: string | boolean) => void) | undefined;
  let bubbleCleanup: (() => void) | undefined;

  try {
    const bubbleContext = useEditorBubbleMenu();
    setBubbleView = bubbleContext.setBubbleView;
    bubbleCleanup = bubbleContext.bubbleCleanup;
  } catch {
    setBubbleView = undefined;
    bubbleCleanup = undefined;
  }

  const isActiveLink = Boolean(editor?.isActive('link'));

  const handleUseAsset = useCallback(
    async (
      items: EditorialAssetSearchResultItem[],
    ): Promise<BinaryUploadResponseData | null> => {
      const selectedItem = items?.[items.length - 1];
      if (!editor || !selectedItem?.uuid) {
        return null;
      }

      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({
          href: buildViewAtSiteUrl(
            activeSite,
            selectedItem.uuid,
            selectedItem.properties?.app || 'editorial',
          ),
          target: '_self',
        })
        .run();

      bubbleCleanup?.();
      return null;
    },
    [editor, activeSite, bubbleCleanup],
  );

  const actionItems = useMemo(
    () => [
      {
        key: 'url' as LinkActionKey,
        label: t('blox.editorial.toolbar.link_to_url'),
      },
      ...(isEditorialAndArticleContent
        ? [
            {
              key: 'asset' as LinkActionKey,
              label: t('blox.editorial.toolbar.link_to_asset'),
            },
          ]
        : []),
      ...(isActiveLink
        ? [
            {
              key: 'remove' as LinkActionKey,
              label: t('blox.editorial.toolbar.remove_link'),
            },
          ]
        : []),
    ],
    [t, isActiveLink, isEditorialAndArticleContent],
  );

  const handleActionSelect = useCallback(
    (index: number) => {
      const selectedAction = actionItems[index]?.key;

      if (!editor || !selectedAction) {
        return;
      }

      if (selectedAction === 'url') {
        openLinkEditor(editor, setBubbleView);
        return;
      }

      if (selectedAction === 'asset') {
        bubbleCleanup?.();

        assetViewContext?.openRelationshipsSearchModal({
          onUseAsset: handleUseAsset,
          selectionMode: 'single',
          footerActionMode: 'addOnly',
        });
        return;
      }

      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    },
    [editor, actionItems, setBubbleView, bubbleCleanup, assetViewContext, handleUseAsset],
  );

  const isDisabled =
    Boolean(disabled) || !editor || (editor.state.selection.empty && !isActiveLink);

  return (
    <SelectDropdown
      options={actionItems.map((item) => item.label)}
      onOptionSelect={handleActionSelect}
      disabled={isDisabled}
      placeholder={<AddLinkIcon />}
      tooltip={t('blox.editorial.toolbar.insert_link')}
      width="64px"
    />
  );
}
